import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { LawAction, LawGetter } from '@/store/law/law.module';
import { LawSchema } from '@/store/law/law.validator';
import { Law } from '@/store/law/law.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'LawFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    lawId: {
      type: Number
    },
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const fileDialog = ref();
    const isEditMode = computed(() => !!props.lawId);
    const law = computed<Law | undefined>(() => store.getters[LawGetter.BY_ID](props.lawId));
    const title = computed(() => isEditMode.value ? 'แก้ไขกฎหมาย/ระเบียบ' : 'เพิ่มกฎหมาย/ระเบียบ');
    const lawForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      title?: string | null;
      pined?: boolean;
      file?: string | null;
    }
    const formData = reactive<Form>({
      id: null,
      title: '',
      pined: false,
      file: '',
    });
    const validator = computed(() => LawSchema.validate(formData, { abortEarly: false }).error);
    const errors = reactive({
      title: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'title')),
      file: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'file')),
    });
    const isValid = computed(() => !errors.title && !errors.file);
    const isLoading = computed(() => store.getters[LawGetter.STATUS] === 'init' || store.getters[LawGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.id = (isEditMode.value) ? props.lawId : null;
        formData.title = law.value?.title;
        formData.pined = (isEditMode.value) ? law.value?.pined : false;
        formData.file = law.value?.file;
        resolve();
      }).then(() => lawForm.value?.show());
    };

    // hide modal
    const hide = () => {
      lawForm.value?.hide();
    };

    // change file
    const fileChange = (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      const file = files?.item(0);

      if (file) {
        formData.file = file.name;
      }
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.title = '';
        formData.pined = false;
        formData.file = '';
        fileDialog.value.value = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(LawAction.UPDATE, {
            id: formData.id,
            title: formData.title,
            pined: formData.pined,
            file: (fileDialog.value?.files?.length !== 0) ? fileDialog.value?.files?.item(0) as File : null,
          }).then(() => hide());
        } else {
          store.dispatch(LawAction.CREATE, {
            title: formData.title,
            pined: formData.pined,
            file: fileDialog.value?.files?.item(0) as File,
          }).then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(LawAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      lawForm,
      isEditMode,
      fileDialog,
      errors,
      submited,
      isLoading,
      show,
      hide,
      fileChange,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
